import * as React from "react";
import { useEffect, useState } from "react";
import { ICSIDUserStatus } from "../../../store/csid/types";
import { ApplicationState } from "../../../store";
import * as services from "../../../store/service";
import * as csidUtil from "../../csid/csid";
import { fetchCreditData } from "../../../util/credit";
import UpdateScore from "../../Credit/CreditScore/UpdateScore";
import CreditScoreAllBureau from "../CreditScore/CreditScoreAllBureau";
import CreditTrackerDashboard from "../CreditScore/CreditTrackerDashboard";
import EZSCreditAuthentication from "./EZSCreditAuthentication";
import IDFCreditAuthentication from "./IDFCreditAuthentication";
import * as CSIDActions from "../../../store/csid/actions";
import * as CreditActions from "../../../store/credit/actions";
import * as UserServiceAction from "../../../store/userService/actions";
import * as dashboardServiceActions from "../../../store/dashboardService/actions";
import { ezsServiceIds, idfServiceIds } from "../../../config/UserServicesList";
import { useAreServicesActive, useAreServicesAndFeatureActive } from "../../../util/CommonServices";
import { useDispatch, useSelector } from "react-redux";
import CreditStatus from "../../Credit/CreditStatus";
import { useTranslation } from "react-i18next";

const DashboardCredit = (props: any) => {
    const dispatch = useDispatch();
    const refreshContext = () => dispatch(CSIDActions.actionCreators.componentNeedsRefresh());
    const refreshCredit = () => dispatch(CreditActions.actionCreators.refreshCredit(true, refreshCallback, reportServiceCode));
    const getUserCSIDStatus = () => dispatch(CSIDActions.actionCreators.getUserCSIDStatus());
    const getUserService = (isForceCall: boolean) => dispatch(UserServiceAction.actionCreators.requestUserService(isForceCall));
    const dashboardServiceData = (isForceCall: boolean) => dispatch(dashboardServiceActions.actionCreators.getDashboardServiceData(isForceCall));
    const hasPurchaseCreditReport = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.hasPurchaseCreditReport);
    const hasOneTimeScore = useSelector((state: ApplicationState) => state.creditHistoryData && state.creditHistoryData.hasOneTimeScore);

    const [hasError, setHasError] = useState(false);
    const [csidUserStatus, setCSIDUserStatus] = useState({} as ICSIDUserStatus);
    const [isLoading, setIsLoading] = useState(false);
    const [showAuthenticatedPage, setShowAuthenticatedPage] = useState(false);
    const [initComplete, setInitComplete] = useState(false);
    const [isIDFuser, setIsIDFUser] = useState(false);
    const [isTUUser, setIsTUUser] = useState(false);
    const [isExperian, setIsExperian] = useState(false);

    const iframeReportAndScoreIds = [
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.creditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianCreditReportwithScore,
    ];
    const isIframeReportAndScore = useAreServicesActive(iframeReportAndScoreIds); // for these services we only get score within report iframe

    const creditScoreServiceList = [
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuOneTimeCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
    ];
    const tuOneTimeCreditServiceIds = [
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuCreditReport,
    ];
    const isCreditScoreActive = useAreServicesActive(creditScoreServiceList) || (hasPurchaseCreditReport && hasOneTimeScore);

    const creditScores = useSelector((state: ApplicationState) => {
        if (state.creditState.creditData && state.creditState.creditData.length > 0) {
            let scoreData = fetchCreditData(state.creditState.creditData, "score");
            
            if (scoreData == null) {
                scoreData = fetchCreditData(state.creditState.creditData);
            }
            return scoreData;
        } else {
            return null;
        }
    });

    const reportServiceCode = creditScores && creditScores["score-service-code"] ? creditScores["score-service-code"] : "";
    const isOneTimeReport = tuOneTimeCreditServiceIds.includes(reportServiceCode);

    const isCreditDataLoading = useSelector((state: ApplicationState) => {
        return state.creditState.isLoading;
    });

    const ids = [
        idfServiceIds.creditScoreMonitoring,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.tuCreditScoreTracker,
        ezsServiceIds.tuCanadaCreditScoreTracker,
        ezsServiceIds.expMonthlyCreditScoreTracker,
    ];

    const creditScoreTrackerIds = [idfServiceIds.creditScoreTracker, ezsServiceIds.tuCreditScoreTracker, ezsServiceIds.expMonthlyCreditScoreTracker];

    let isCreditScoreTrackerActive = useAreServicesAndFeatureActive(ids, ["Product.CreditScoreTracker"]);
    const idfCreditTrackerActive = useAreServicesActive(creditScoreTrackerIds);

    if (isIDFuser) {
        isCreditScoreTrackerActive = idfCreditTrackerActive;
    }

    const renderScoreTracker = () => {
        if (isCreditScoreActive && isCreditScoreTrackerActive) {
            if (!isCreditDataLoading) {
                return <CreditTrackerDashboard isIDFUser={isIDFuser} />;
            }
        } else if (isCreditScoreTrackerActive) {
            return <CreditTrackerDashboard isIDFUser={isIDFuser} />;
        }
    };

    const refreshComponent = () => {
        setIsLoading(true);
        refreshCredit();
    };

    const refreshCallback = () => {
        // recall APIs to get latest update date
        getUserService(true);
        dashboardServiceData(true);
        setIsLoading(false);
    };
    const { t: tLang } = useTranslation();

    useEffect(() => {
        setIsLoading(true);
        services
            .getUserCSIDStatus()
            .then((data) => {
                if (data.StatusCode === 500) {
                    setIsLoading(false);
                    setHasError(true);
                } else {
                    setIsLoading(false);
                    const csidUserStatus: ICSIDUserStatus = data as ICSIDUserStatus;
                    setCSIDUserStatus(csidUserStatus);

                    setIsIDFUser(csidUserStatus && csidUserStatus.isIDFUser ? csidUserStatus.isIDFUser : false);
                    setIsTUUser(csidUserStatus && csidUserStatus.isTUUser ? csidUserStatus.isTUUser : false);
                    setIsExperian(
                        csidUserStatus && csidUserStatus.l3AuthProvider && csidUserStatus.l3AuthProvider.isExperian
                            ? csidUserStatus.l3AuthProvider.isExperian
                            : false
                    );

                    if ((csidUserStatus && csidUserStatus.isIDFUser) || (csidUserStatus && csidUserStatus.isTUUser)) {
                        if (csidUserStatus["auth-status"] != null && csidUserStatus["auth-status"].trim().toUpperCase() === "AUTHENTICATED") {
                            setShowAuthenticatedPage(true);
                        }
                    } else {
                        const nextStep: csidUtil.NEXT_STEP = csidUtil.getNextStep(csidUserStatus as ICSIDUserStatus);
                        switch (nextStep) {
                            case csidUtil.NEXT_STEP.GO_TO_DASHBOARD:
                                setShowAuthenticatedPage(true);
                                break;
                            default:
                                break;
                        }
                    }

                    if (initComplete === false) {
                        setInitComplete(true);
                    }
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setHasError(true);

                if (initComplete === false) {
                    setInitComplete(true);
                }
            });
    }, []);
    const partnerPhoneNumber: string = useSelector((state: ApplicationState) =>
    state.configurationState && state.configurationState.configuration.partnerConfig.phoneNumber
        ? state.configurationState.configuration.partnerConfig.phoneNumber
        : ""
);

const renderMessageScreen = () => {
    
    if (creditScores && (creditScores.IsIDFUser || isTUUser) && (creditScores["tu-status"] === "tu-thin-error" || creditScores["tu-status"] === "tu-down")) {
        return <CreditStatus   status={creditScores["tu-status"]} />;
    } else {
        return null;
    }
};


    const creditAuthSuccessCallBack = (callbackObj: any) => {
        getUserCSIDStatus();
        refreshContext();
    };

    const closeCreditAuthCallBack = (callbackObj: any) => {
        refreshContext();
    };

    const getTitle = () => {
        let title = "";
        if (isCreditScoreActive) {
            title = tLang("dashboard.creditScore.title");
        } else if (isCreditScoreTrackerActive) {
            title = tLang("dashboard.creditScore.trackerTitle");
        }
        return title;
    };

    const renderCreditAuthentication = () => {
        return (
            <div className="box">
                <div className="box-header">
                    <div className="box-title-container row between-xs">
                        <div className="box-title-text has-actions">
                            <h2 className="box-title heading-title">{getTitle()}</h2>
                        </div>
                    </div>
                </div>

                <div className={`box-content ${isLoading && "loading"}`}>
                    {initComplete && (
                        <React.Fragment>
                            {csidUserStatus && (isTUUser || isIDFuser) && (
                                <IDFCreditAuthentication
                                    initCSIDUserStatus={csidUserStatus as ICSIDUserStatus}
                                    creditAuthCallback={creditAuthSuccessCallBack}
                                    displayFlowAsDialog={true}
                                />
                            )}
                            {csidUserStatus && isExperian && (
                                <EZSCreditAuthentication
                                    initCSIDUserStatus={csidUserStatus as ICSIDUserStatus}
                                    creditAuthCallback={creditAuthSuccessCallBack}
                                    displayFlowAsDialog={true}
                                    componentParent={"dashboard"}
                                    onCloseCallback={closeCreditAuthCallBack}
                                    l3Case={"credit-service"}
                                />
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>
        );
    };

    return hasError ? (
        <div className="box">
            <div className="box-header">
                <div className="box-title-container row between-xs">
                    <div className="box-title-text has-actions">
                        <h2 className="box-title">{getTitle()}</h2>
                    </div>
                </div>
            </div>
            <div className="box offset-padding">
                <div className="box-header">
                    <div className="box-title-container">
                        <div className="box-title-icon dashboard-alert-icon" aria-hidden="true">
                            <em className="las la-exclamation-triangle"></em>
                        </div>
                        <div className="box-title-text has-actions">
                            <h2 className="box-title">{tLang("credit.authErrorHeader")}</h2>
                        </div>
                    </div>
                </div>
                <div className="box-content">
                    <p>{tLang("credit.authErrorMessage")}</p>
                </div>
            </div>
        </div>
    ) : (
        <React.Fragment>
            {!isIframeReportAndScore && (isCreditScoreActive || isCreditScoreTrackerActive) && !showAuthenticatedPage && renderCreditAuthentication()}

            {!isIframeReportAndScore && isCreditScoreActive && showAuthenticatedPage && (
                <div className="box">
                    <div className="box-header">
                        <div className="box-title-container row between-xs">
                            <div className="box-title-text has-actions">
                                <h2 className="box-title">{getTitle()}</h2>
                            </div>
                           {creditScores != null  && (creditScores["tu-status"] !== "tu-thin-error" && creditScores["tu-status"] !== "tu-down" && creditScores["tu-status"] !== "TU-Fulfill-Pull-Error")  ? (
                             <React.Fragment>
                                    {showAuthenticatedPage && isTUUser && (
                                        <UpdateScore
                                            isDashboardView={true}
                                            updateComponent={refreshComponent}
                                            updateDate={creditScores.IsIDFUser ? creditScores["update-date"] : creditScores["score-update-date"]}
                                            nextAvailableUpdate={creditScores.IsIDFUser ? creditScores["next-update-date"] : creditScores["next-score-update-date"]}
                                            serviceCode={reportServiceCode}
                                            isIDFUser={creditScores.IsIDFUser}
                                            isTUUser={isTUUser}
                                            combinationRefresh={false}
                                            isOneTimeReport={isOneTimeReport}
                                            tuStatus={creditScores["tu-status"]}
                                        />
                                    )}
                                </React.Fragment>
                            ) : (renderMessageScreen())
                            }     
                        </div>
                    </div>
                    <div className={`box-content ${isLoading && "loading"}`}>
                        {initComplete && <React.Fragment>{showAuthenticatedPage && <CreditScoreAllBureau />}</React.Fragment>}
                    </div>
                </div>
            )}
            {initComplete && showAuthenticatedPage && renderScoreTracker()}
        </React.Fragment>
    );
};
export default DashboardCredit;
